// services/AdminProviderService.js - ReactJS (web)
// Provider Management for Admin - Only provider-related routes

import api from '../api/api';

class AdminProviderService {
  constructor() {
    // Store api reference
    this.api = api;
    
    // Token keys for localStorage
    this.TOKEN_KEYS = {
      ACCESS_TOKEN: 'admin_access_token',
      REFRESH_TOKEN: 'admin_refresh_token',
      USER_DATA: 'admin_user_data',
      TOKEN_EXPIRY: 'admin_token_expiry',
      USER_TYPE: 'admin_user_type', // To identify this is admin data
    };
    
    // Cache keys for localStorage
    this.CACHE_KEYS = {
      PROVIDERS: 'admin_providers_cache',
      PROVIDER_STATS: 'admin_provider_stats_cache',
      PROVIDER_DETAIL: 'admin_provider_detail_cache'
    };
    
    // Cache expiration times (in milliseconds)
    this.CACHE_EXPIRY = {
      SHORT: 5 * 60 * 1000,    // 5 minutes
      MEDIUM: 15 * 60 * 1000,  // 15 minutes
      LONG: 60 * 60 * 1000,    // 1 hour
      EXTRA_LONG: 24 * 60 * 60 * 1000 // 24 hours
    };
  }

  // ==================== TOKEN HELPER METHODS ====================

  /**
   * Get access token from localStorage
   */
  getAccessToken() {
    try {
      return localStorage.getItem(this.TOKEN_KEYS.ACCESS_TOKEN);
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    const token = this.getAccessToken();
    if (!token) return false;
    
    const expiry = localStorage.getItem(this.TOKEN_KEYS.TOKEN_EXPIRY);
    if (expiry && Date.now() > parseInt(expiry)) {
      return false;
    }
    
    return true;
  }

  /**
   * Redirect to login page
   */
  redirectToLogin() {
    try {
      Object.values(this.TOKEN_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      
      window.dispatchEvent(new CustomEvent('auth:logout'));
      
      const loginPath = '/admin/login';
      if (window.location.pathname !== loginPath) {
        window.location.href = loginPath;
      }
    } catch (error) {
      console.error('[AdminProviderService] Error redirecting to login:', error);
      window.location.href = '/admin/login';
    }
  }

  /**
   * Check authentication before making request
   */
  checkAuth() {
    if (!this.isAuthenticated()) {
      this.redirectToLogin();
      throw new Error('Authentication required. Please login again.');
    }
    return true;
  }

  // ==================== CACHE HELPER METHODS ====================

  setCacheItem(key, data, expiry = this.CACHE_EXPIRY.MEDIUM) {
    try {
      const cacheItem = {
        data,
        timestamp: Date.now(),
        expiry
      };
      localStorage.setItem(key, JSON.stringify(cacheItem));
      return true;
    } catch (error) {
      console.error('Error setting cache item:', error);
      return false;
    }
  }

  getCacheItem(key) {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;

      const cacheItem = JSON.parse(item);
      const now = Date.now();
      
      if (now - cacheItem.timestamp > cacheItem.expiry) {
        localStorage.removeItem(key);
        return null;
      }

      return cacheItem.data;
    } catch (error) {
      console.error('Error getting cache item:', error);
      return null;
    }
  }

  clearCacheItem(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error clearing cache item:', error);
      return false;
    }
  }

  clearAllCache() {
    try {
      Object.values(this.CACHE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      console.error('Error clearing all cache:', error);
      return false;
    }
  }

  invalidateCache(keyPattern) {
    try {
      Object.keys(this.CACHE_KEYS).forEach(key => {
        if (key.includes(keyPattern) || key === keyPattern) {
          localStorage.removeItem(this.CACHE_KEYS[key]);
        }
      });
      return true;
    } catch (error) {
      console.error('Error invalidating cache:', error);
      return false;
    }
  }

  async getCachedOrFetch(cacheKey, fetchFn, expiry = this.CACHE_EXPIRY.MEDIUM) {
    const cachedData = this.getCacheItem(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const freshData = await fetchFn();
    
    if (freshData && freshData.success !== false) {
      this.setCacheItem(cacheKey, freshData, expiry);
    }
    
    return freshData;
  }

  // ==================== ADMIN PROVIDER ROUTES ====================
  // Routes that exist in adminRoutes.js

  /**
   * Get all service providers with filtering, sorting, and pagination (Admin view)
   * GET /api/providers
   */
  async getAllProviders(params = {}, useCache = true) {
    try {
      this.checkAuth();
      
      const cacheKey = `${this.CACHE_KEYS.PROVIDERS}_${JSON.stringify(params)}`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await this.api.get('/providers', { params });
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.SHORT);
      }
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        this.redirectToLogin();
      }
      throw this.handleError(error);
    }
  }

  /**
   * Get service provider by ID (Admin view)
   * GET /api/providers/:id
   */
  async getProviderById(id, populate = null, useCache = true) {
    try {
      this.checkAuth();
      
      const cacheKey = `${this.CACHE_KEYS.PROVIDER_DETAIL}_${id}_${populate || ''}`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const params = populate ? { populate } : {};
      const response = await this.api.get(`/admin-providers/${id}`, { params });
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.MEDIUM);
      }
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        this.redirectToLogin();
      }
      throw this.handleError(error);
    }
  }

  /**
   * Delete any service provider
   * DELETE /api/providers/:id
   */
  async deleteProvider(id) {
    try {
      this.checkAuth();
      
      const response = await this.api.delete(`/admin-providers/${id}`);
      
      this.invalidateCache('PROVIDERS');
      this.invalidateCache('PROVIDER_STATS');
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        this.redirectToLogin();
      }
      throw this.handleError(error);
    }
  }

  /**
   * Get any provider statistics
   * GET /api/providers/:id/statistics
   */
  async getProviderStatistics(id, useCache = true) {
    try {
      this.checkAuth();
      
      const cacheKey = `${this.CACHE_KEYS.PROVIDER_STATS}_${id}`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await this.api.get(`/admin-providers/${id}/statistics`);
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.MEDIUM);
      }
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        this.redirectToLogin();
      }
      throw this.handleError(error);
    }
  }

  /**
   * Bulk update provider status
   * PATCH /api/providers/bulk/status
   */
  async bulkUpdateProviderStatus(ids, status, reason = null) {
    try {
      this.checkAuth();
      
      const response = await this.api.patch('/admin-providers/bulk/status', { ids, status, reason });
      
      this.invalidateCache('PROVIDERS');
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        this.redirectToLogin();
      }
      throw this.handleError(error);
    }
  }

  /**
   * Update provider verification status
   * PATCH /api/providers/:id/verification
   */
  async updateProviderVerificationStatus(id, status, reason = null) {
    try {
      console.log('🔐 Updating verification status for provider:', id);
      console.log('📝 Status:', status);
      
      this.checkAuth();
      
      console.log('✅ Authentication verified, sending request...');

      const response = await this.api.patch(`/admin-providers/${id}/verification`, { status, reason });
      
      console.log('✅ Verification status update response:', response.data);
      
      this.invalidateCache('PROVIDERS');
      this.invalidateCache('PROVIDER_DETAIL');
      this.invalidateCache('PROVIDER_STATS');
      
      return response.data;
    } catch (error) {
      console.error('❌ Error updating verification status:', error);
      
      if (error.response?.status === 401) {
        console.log('🔐 Unauthorized, redirecting to login...');
        this.redirectToLogin();
      }
      throw this.handleError(error);
    }
  }

  /**
   * Toggle featured status
   * PATCH /api/providers/:id/featured
   */
  async toggleProviderFeatured(id, isFeatured, duration = 30) {
    try {
      this.checkAuth();
      
      const response = await this.api.patch(`/admin-providers/${id}/featured`, { isFeatured, duration });
      
      this.invalidateCache('PROVIDERS');
      this.invalidateCache('PROVIDER_DETAIL');
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        this.redirectToLogin();
      }
      throw this.handleError(error);
    }
  }

  /**
   * Get providers by user ID (admin can view any user's providers)
   * GET /api/providers/user/:userId
   */
  async getProvidersByUserId(userId, useCache = true) {
    try {
      this.checkAuth();
      
      const cacheKey = `${this.CACHE_KEYS.PROVIDERS}_user_${userId}`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await this.api.get(`/admin-providers/user/${userId}`);
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.MEDIUM);
      }
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        this.redirectToLogin();
      }
      throw this.handleError(error);
    }
  }

  /**
   * Clear location cache
   * POST /api/providers/clear-cache
   */
  async clearLocationCache() {
    try {
      this.checkAuth();
      
      const response = await this.api.post('/admin-providers/clear-cache');
      
      this.clearAllCache();
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        this.redirectToLogin();
      }
      throw this.handleError(error);
    }
  }

  // ==================== REMOVED METHODS ====================
  // These routes don't exist in your adminRoutes.js:
  // ❌ getProvidersOverview - /providers/statistics/overview doesn't exist
  // ❌ getProvidersByServiceType - /providers/type/:serviceType doesn't exist
  // ❌ getFeaturedProviders - /providers/featured doesn't exist
  // ❌ getProvidersByStatus - /providers/status/:status doesn't exist
  // ❌ getProviderFilters - /providers/filters doesn't exist
  // ❌ exportProvidersData - /providers/export doesn't exist

  // ==================== HELPER METHODS ====================

  handleError(error) {
    if (error.response?.status === 401) {
      this.redirectToLogin();
      return {
        success: false,
        statusCode: 401,
        message: 'Session expired. Please login again.',
        requiresLogin: true,
      };
    }
    
    if (error.response) {
      return {
        success: false,
        statusCode: error.response.status,
        message: error.response.data?.message || error.response.data?.error || 'An error occurred',
        data: error.response.data,
      };
    } else if (error.request) {
      return {
        success: false,
        message: 'No response from server. Please check your connection.',
        error: error.request,
      };
    } else {
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
        error: error,
      };
    }
  }

  getServiceTypes() {
    return [
      'Electrician', 'Plumber', 'Mason', 'Painter', 'Carpenter', 
      'Barber', 'Hairdresser', 'Mechanic', 'HVAC Technician', 
      'Interior Designer', 'Landscaper', 'Cleaner', 'Handyman', 
      'Tiler', 'Solar Installer', 'Flooring Specialist', 'Other'
    ];
  }

  getVerificationStatuses() {
    return ['pending', 'verified', 'rejected', 'suspended'];
  }

  getSupportedLanguages() {
    return ['en', 'ht', 'fr', 'es'];
  }

  getProviderStatuses() {
    return ['active', 'inactive', 'suspended', 'pending'];
  }

  getFeaturedOptions() {
    return [
      { value: true, label: 'Featured' },
      { value: false, label: 'Not Featured' }
    ];
  }
}

export default new AdminProviderService();